<template>
  <div :class="['game-page', themeClass]" :lang="appStore.language">
    <!-- LEFT: Game Info + Description -->
    <div class="left-column">
      <div class="header-section">
        <h1 class="title">{{ game.name[appStore.language] }}</h1>
      </div>
      
      <div class="image-section">
        <div class="image-wrapper" v-if="game.image">
          <img :src="game.image" alt="Game Cover" class="game-image" />
          <div class="player-count">
            <span class="material-icons">person</span>
            {{ game.minPlayers === game.maxPlayers
              ? game.minPlayers
              : `${game.minPlayers}-${game.maxPlayers}` }}
          </div>
        </div>
      </div>

      <div class="description-section">
        <p class="description">{{ game?.description?.[appStore.language] }}</p>
      </div>
    </div>

    <!-- RIGHT: QR Code + Players -->
    <div class="right-column">
      <div class="qr-container">
        <p class="subtitle">{{ t('game.scan') }}</p>
        <div class="qr-box">
          <QrcodeVue :value="joinUrl" :size="qrSize" />
        </div>
      </div>

      <div class="players-container">
        <p class="subtitle">{{ t('game.players_joined') }} ({{ players.length }})</p>
        <div class="player-grid">
          <div class="player-card" v-for="(player, index) in players" :key="index">
            <template v-if="player.image">
              <img :src="player.image" alt="Avatar" class="avatar" />
            </template>
            <template v-else>
              <div class="avatar initials" 
                :style="{ backgroundColor: player.color || '#555'}">
                {{ getInitials(player.name || 'P') }}
              </div>
            </template>
            <p class="player-name">{{ player.name || 'Player' }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import QrcodeVue from 'qrcode.vue'
import eventBus from '../plugins/eventBus'
import { useAppStore } from '@/stores/appStore'
import { useI18n } from 'vue-i18n'
import { db } from '../plugins/firebase';
import { doc, getDoc } from 'firebase/firestore';

defineOptions({ name: 'GamePage' })

const { t } = useI18n()

const appStore = useAppStore()
const width = ref(window.innerWidth)
const height = ref(window.innerHeight)

const game = ref({id:'', name:{en:'', el:''}, image:'', type:''})
const joinUrl = computed(() => `https://smeegoon.com/app/join?room=${encodeURIComponent(appStore.name || '')}`)
const players = ref([])

// Dynamically calculate QR size based on screen height to ensure it fits
const qrSize = computed(() => Math.min(height.value * 0.25, 300))

const themeClass = computed(() => (appStore.theme === 'light' ? 'light-theme' : 'dark-theme'))

const getInitials = (name) => {
  const words = name.trim().split(/\s+/);
  return words.length === 1
    ? words[0][0].toUpperCase()
    : words.map(w => w[0].toUpperCase()).join('').slice(0, 2);
};

onMounted(async () => {
  eventBus.on('start', handleStart)
  window.addEventListener('resize', handleResize)
  
  const gameRef = doc(db, 'games', appStore.gameId)
  const gameSnap = await getDoc(gameRef)
  if (gameSnap.exists()) {
    game.value = gameSnap.data();
  }
})

onBeforeUnmount(() => {
  eventBus.off('start')
  window.removeEventListener('resize', handleResize)
})

function handleResize() {
  width.value = window.innerWidth
  height.value = window.innerHeight
}

async function handleStart(params) {
  console.log('Game started payload:', params)
}
</script>

<style scoped>
.game-page {
  display: flex;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
}

.dark-theme {
  background-color: #000;
  color: white;
}

.light-theme {
  background-color: #fff;
  color: black;
}

/* COLUMN LAYOUT */
.left-column {
  flex: 6;
  display: flex;
  flex-direction: column;
  padding: 4vh 4vw;
  border-right: 1px solid rgba(128, 128, 128, 0.2);
}

.right-column {
  flex: 4;
  display: flex;
  flex-direction: column;
  padding: 4vh 2vw;
  align-items: center;
  justify-content: flex-start;
  gap: 4vh;
}

/* LEFT COLUMN COMPONENTS */
.header-section {
  flex: 0 0 auto;
}

.title {
  font-size: 3rem;
  margin: 0 0 2vh 0;
  font-weight: bold;
}

.image-section {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0; /* Important for flex child to shrink */
  margin-bottom: 2vh;
}

.image-wrapper {
  position: relative;
  max-height: 100%;
  max-width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
}

.game-image {
  max-height: 50vh;
  width: auto;
  max-width: 100%;
  display: block;
}

.description-section {
  flex: 0 0 auto;
}

.description {
  font-size: 1.4rem;
  line-height: 1.5;
  margin: 0;
  opacity: 0.9;
}

/* RIGHT COLUMN COMPONENTS */
.qr-container {
  text-align: center;
  flex: 0 0 auto;
}

.qr-box {
  background: white;
  padding: 15px;
  border-radius: 15px;
  display: inline-block;
  margin-top: 1vh;
}

.subtitle {
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0 0 1vh 0;
}

.players-container {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.player-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  overflow-y: auto;
  padding-right: 5px;
  scrollbar-width: none; /* Firefox */
}

.player-grid::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}

.player-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: rgba(128, 128, 128, 0.1);
  padding: 10px;
  border-radius: 12px;
}

.avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #333;
}

.avatar.initials {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.5rem;
  color: white;
}

.player-name {
  font-size: 1.1rem;
  margin: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.player-count {
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 15px;
  border-radius: 10px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>

